import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useDefinitionContext } from "./context";

export function useBlockLevelState(setParentLevel) {
  const registeredRef = useRef(null)
  const [inheritance, setInheritance] = useState({})

  const getLevel = useCallback(() => {
    for (const level of PRIORITY) {
      if (inheritance[level] > 0) {
        return level
      }
    }
    return null
  }, [inheritance])

  const setLevel = useCallback((level, value) => {
    if (!level || !value) {
      return;
    }
    setInheritance(state => ({
      ...state,
      [level]: (state[level] || 0) + value,
    }))
    setParentLevel?.(level, value)
  }, [setParentLevel])

  const register = useCallback(level => {
    const registered = registeredRef.current
    if (registered === level) return;
    if (registered) setLevel(registered, -1)
    if (level) setLevel(level, 1)
    registeredRef.current = level
  }, [setLevel])

  return useMemo(() => ({
    getLevel,
    setLevel,
    register,
  }), [getLevel, setLevel, register])
}

export function useDeclareBlockLevel(level) {
  const { blockLevel: { register } = {} } = useDefinitionContext()
  useEffect(() => {
    register(level)
    return () => register(null)
  }, [level, register])
}

export function useReadBlockLevel() {
  const { definition: { context: { useValue } } } = useDefinitionContext()
  const context = useValue?.()
  return useMemo(() => context?.blockLevel, [context])
}

const PRIORITY = [
  'site',
  'organization',
]
